import { ALIACE_SPA_ID } from "~/constants/companies";
import { supabase } from "~/integrations/supabase/client";
import { handleError } from "~/lib/errorHandler";

import { resolveDiscountAmount, resolveLineAmounts } from "~/types/purchasing";

import type {
  DiscountType,
  OrderType,
  Plant,
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderFormData,
  PurchaseOrderItem,
  PurchaseOrderReception,
  PurchaseOrderStatus,
  ReceptionFormData,
  ReceptionWithOrder,
} from "~/types/purchasing";

const TAX_RATE = 0.19; // 19% IVA

// Validate that items match the order type
function validateOrderTypeConsistency(
  orderType: OrderType,
  items: PurchaseOrderFormData["items"]
): void {
  for (const item of items) {
    if (orderType === "products" && item.ingredient_type !== "product") {
      throw new Error(
        "No se pueden agregar ingredientes a una orden de productos"
      );
    }
    if (orderType === "ingredients" && item.ingredient_type === "product") {
      throw new Error(
        "No se pueden agregar productos a una orden de ingredientes"
      );
    }
    // Supplies ("Misceláneos") orders are free-text lines: every item is a "misc"
    // ingredient with no catalog reference. "raw_material" is also accepted for
    // legacy/edited rows. Anything else (oil/product) is invalid here.
    if (
      orderType === "supplies" &&
      item.ingredient_type !== "misc" &&
      item.ingredient_type !== "raw_material"
    ) {
      throw new Error(
        "Solo se pueden agregar misceláneos a una orden de misceláneos"
      );
    }
  }
}

// Build the persisted item columns from a form line (shared by create + update). The per-line
// discount reduces the line gross to its net subtotal (quantity * unit_price - discount_amount),
// mirroring the cotización "Rec/Desc" column; discount_amount is frozen alongside subtotal.
function buildItemColumns(item: PurchaseOrderFormData["items"][number]) {
  const discount_type: DiscountType = item.discount_type ?? "none";
  const discount_value = item.discount_value ?? 0;
  const { discount, net } = resolveLineAmounts(
    item.quantity,
    item.unit_price,
    discount_type,
    discount_value
  );
  return {
    ingredient_type: item.ingredient_type,
    oil_type_id: item.oil_type_id ?? null,
    raw_material_id: item.raw_material_id ?? null,
    product_id: item.product_id ?? null,
    description: item.description,
    quantity: item.quantity,
    unit: item.unit,
    unit_price: item.unit_price,
    discount_type,
    discount_value,
    discount_amount: discount,
    subtotal: net,
    ila_rate: item.ila_rate ?? 0,
  };
}

export const purchaseOrderService = {
  async getAll(filters?: PurchaseOrderFilters): Promise<PurchaseOrder[]> {
    let query = supabase
      .from("purchase_orders")
      .select(
        "*, supplier:suppliers(*), items:purchase_order_items(quantity, received_quantity)"
      )
      .order("issue_date", { ascending: false });

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    if (filters?.supplier_id) {
      query = query.eq("supplier_id", filters.supplier_id);
    }

    if (filters?.dateFrom) {
      query = query.gte("issue_date", filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte("issue_date", filters.dateTo);
    }

    if (filters?.order_type) {
      query = query.eq("order_type", filters.order_type);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as PurchaseOrder[];
  },

  async getById(id: string): Promise<PurchaseOrder> {
    const { data, error } = await supabase
      .from("purchase_orders")
      .select(
        `
        *,
        supplier:suppliers(*),
        items:purchase_order_items(
          *,
          oil_type:oil_types(*),
          raw_material:raw_materials(*),
          product:products(id, short_name, barcode, net_price)
        )
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as PurchaseOrder;
  },

  async getByPoNumber(poNumber: number): Promise<PurchaseOrder> {
    const { data, error } = await supabase
      .from("purchase_orders")
      .select(
        `
        *,
        supplier:suppliers(*),
        items:purchase_order_items(
          *,
          oil_type:oil_types(*),
          raw_material:raw_materials(*),
          product:products(id, short_name, barcode, net_price)
        )
      `
      )
      .eq("po_number", poNumber)
      .single();

    if (error) throw error;
    return data as PurchaseOrder;
  },

  calculateTotals(
    items: {
      quantity: number;
      unit_price: number;
      ila_rate?: number;
      discount_type?: DiscountType;
      discount_value?: number;
    }[],
    discount?: { type?: DiscountType; value?: number },
    isExento?: boolean
  ): {
    subtotal: number;
    discount_amount: number;
    tax_amount: number;
    ila_amount: number;
    total: number;
  } {
    // Resolve each line's NET amount first (gross − per-line discount, the cotización Rec/Desc).
    const lines = items.map((item) => ({
      net: resolveLineAmounts(
        item.quantity,
        item.unit_price,
        item.discount_type,
        item.discount_value
      ).net,
      ila_rate: item.ila_rate ?? 0,
    }));
    // Header subtotal is the sum of the net lines (the neto after per-line discounts).
    const subtotal = lines.reduce((sum, line) => sum + line.net, 0);
    // Order-level discount comes off the neto BEFORE IVA (the IVA base is the discounted neto).
    const discount_amount = resolveDiscountAmount(
      subtotal,
      discount?.type,
      discount?.value
    );
    // Exento de IVA: a VAT-exempt cotización adds no 19% IVA (the whole neto is exento).
    const tax_amount = isExento
      ? 0
      : Math.round((subtotal - discount_amount) * TAX_RATE);
    // ILA is a per-line additional tax (after IVA); sum each line's rounded amount, computed on
    // the net line subtotal (post per-line discount) and not reduced by the order-level discount.
    const ila_amount = lines.reduce(
      (sum, line) => sum + Math.round((line.net * line.ila_rate) / 100),
      0
    );
    const total = subtotal - discount_amount + tax_amount + ila_amount;

    return { subtotal, discount_amount, tax_amount, ila_amount, total };
  },

  async create(data: PurchaseOrderFormData): Promise<PurchaseOrder> {
    // Validate order type consistency
    validateOrderTypeConsistency(data.order_type, data.items);

    // Calculate totals (order-level discount applies to the neto before IVA; exento ⇒ no IVA)
    const discount_type: DiscountType = data.discount_type ?? "none";
    const discount_value = data.discount_value ?? 0;
    const is_exento = data.is_exento ?? false;
    const { subtotal, discount_amount, tax_amount, ila_amount, total } =
      this.calculateTotals(
        data.items,
        {
          type: discount_type,
          value: discount_value,
        },
        is_exento
      );

    // Create PO header. company_id postdates the generated types — cast the payload.
    const { data: po, error: poError } = await supabase
      .from("purchase_orders")
      .insert({
        supplier_id: data.supplier_id,
        company_id: data.company_id ?? ALIACE_SPA_ID,
        order_type: data.order_type,
        issue_date: data.issue_date,
        currency: data.currency,
        exchange_rate_id: data.exchange_rate_id ?? null,
        payment_terms: data.payment_terms ?? null,
        observations: data.observations ?? null,
        subtotal,
        discount_type,
        discount_value,
        discount_amount,
        is_exento,
        tax_amount,
        ila_amount,
        total,
        status: "draft",
      } as never)
      .select()
      .single();

    if (poError) throw poError;
    const poRow = po as { id: string };

    // Create PO items (net subtotal + per-line discount frozen via buildItemColumns)
    const itemsToInsert = data.items.map((item) => ({
      purchase_order_id: poRow.id,
      ...buildItemColumns(item),
    }));

    const { error: itemsError } = await supabase
      .from("purchase_order_items")
      .insert(itemsToInsert);

    if (itemsError) throw itemsError;

    return this.getById(poRow.id);
  },

  async update(
    id: string,
    data: Partial<PurchaseOrderFormData>
  ): Promise<PurchaseOrder> {
    // Check if PO can be edited (only draft status)
    const { data: current, error: fetchError } = await supabase
      .from("purchase_orders")
      .select("status")
      .eq("id", id)
      .single();

    if (fetchError) throw fetchError;

    if (current?.status !== "draft") {
      throw new Error(
        "No se puede editar la orden de compra después de enviada"
      );
    }

    const updates: Record<string, unknown> = {};

    if (data.supplier_id) updates.supplier_id = data.supplier_id;
    if (data.issue_date) updates.issue_date = data.issue_date;
    if (data.currency) updates.currency = data.currency;
    if (data.exchange_rate_id !== undefined)
      updates.exchange_rate_id = data.exchange_rate_id;
    if (data.payment_terms !== undefined)
      updates.payment_terms = data.payment_terms;
    if (data.observations !== undefined)
      updates.observations = data.observations;

    // If items are provided, recalculate totals and update items using smart upsert.
    // The edit form always resubmits the full item list, so the order-level discount is
    // recomputed here off the fresh subtotal (discount applies to the neto before IVA).
    if (data.items) {
      const discount_type: DiscountType = data.discount_type ?? "none";
      const discount_value = data.discount_value ?? 0;
      const is_exento = data.is_exento ?? false;
      const { subtotal, discount_amount, tax_amount, ila_amount, total } =
        this.calculateTotals(
          data.items,
          {
            type: discount_type,
            value: discount_value,
          },
          is_exento
        );
      updates.subtotal = subtotal;
      updates.discount_type = discount_type;
      updates.discount_value = discount_value;
      updates.discount_amount = discount_amount;
      updates.is_exento = is_exento;
      updates.tax_amount = tax_amount;
      updates.ila_amount = ila_amount;
      updates.total = total;

      // Get existing items with their reception counts
      const { data: existingItems } = await supabase
        .from("purchase_order_items")
        .select(
          `
          id,
          purchase_order_reception_items(id)
        `
        )
        .eq("purchase_order_id", id);

      // Build set of items with receptions (cannot be deleted due to FK constraint)
      const itemsWithReceptions = new Set(
        existingItems
          ?.filter(
            (item) =>
              item.purchase_order_reception_items &&
              item.purchase_order_reception_items.length > 0
          )
          .map((item) => item.id) ?? []
      );

      // Existing item IDs in database
      const existingItemIds = new Set(
        existingItems?.map((item) => item.id) ?? []
      );

      // Separate form items into updates vs inserts
      const itemsToUpdate = data.items.filter(
        (item) => item.id && existingItemIds.has(item.id)
      );
      const itemsToInsert = data.items.filter(
        (item) => !item.id || !existingItemIds.has(item.id)
      );

      // Delete items that are NOT in form AND have no receptions
      const formItemIds = new Set(
        data.items.filter((i) => i.id).map((i) => i.id)
      );
      const idsToDelete =
        existingItems
          ?.filter(
            (item) =>
              !formItemIds.has(item.id) && !itemsWithReceptions.has(item.id)
          )
          .map((item) => item.id) ?? [];

      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from("purchase_order_items")
          .delete()
          .in("id", idsToDelete);

        if (deleteError) throw deleteError;
      }

      // Update existing items
      for (const item of itemsToUpdate) {
        const { error: updateError } = await supabase
          .from("purchase_order_items")
          .update(buildItemColumns(item))
          .eq("id", item.id!);

        if (updateError) throw updateError;
      }

      // Insert new items
      if (itemsToInsert.length > 0) {
        const newItems = itemsToInsert.map((item) => ({
          purchase_order_id: id,
          ...buildItemColumns(item),
        }));

        const { error: itemsError } = await supabase
          .from("purchase_order_items")
          .insert(newItems);

        if (itemsError) throw itemsError;
      }
    }

    if (Object.keys(updates).length > 0) {
      const { error } = await supabase
        .from("purchase_orders")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    }

    return this.getById(id);
  },

  async updateStatus(
    id: string,
    status: PurchaseOrderStatus
  ): Promise<PurchaseOrder> {
    const updates: Record<string, unknown> = { status };

    // If marking as received, set received_at timestamp
    if (status === "received") {
      updates.received_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from("purchase_orders")
      .update(updates)
      .eq("id", id);

    if (error) throw error;

    return this.getById(id);
  },

  async markAsReceived(id: string): Promise<PurchaseOrder> {
    // Update status
    const po = await this.updateStatus(id, "received");

    // Update stock via database function
    const { error } = await supabase.rpc("update_stock_from_po", {
      p_purchase_order_id: id,
    });

    if (error) throw error;

    return po;
  },

  /**
   * Create a new reception with items (supports partial receptions with variable costs).
   *
   * Runs atomically through the `register_po_reception` RPC, which inserts the reception +
   * items, moves legacy stock (`process_reception_stock`, honouring `exclude_from_inventory`),
   * and — for SKUs of the "Aliace Pruebas" test company only — mirrors the reception into the
   * new inventory ledger (`bridge_reception_item_to_inv` → `inv_create_reception`). Real-company
   * receptions are untouched by the bridge.
   */
  async createReception(
    data: ReceptionFormData
  ): Promise<PurchaseOrderReception> {
    // Filter out items with 0 quantity
    const itemsToReceive = data.items.filter(
      (item) => item.quantity_received > 0
    );
    if (itemsToReceive.length === 0) {
      throw new Error("Debe recibir al menos un item");
    }

    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data: reception, error } = await supabase.rpc(
      "register_po_reception",
      {
        p_purchase_order_id: data.purchase_order_id,
        p_reception_date: data.reception_date,
        p_plant: data.plant,
        p_observations: data.observations ?? null,
        p_received_by: user?.id ?? null,
        p_items: itemsToReceive.map((item) => ({
          purchase_order_item_id: item.purchase_order_item_id,
          quantity_received: item.quantity_received,
          actual_unit_cost: item.actual_unit_cost,
          expiry_date: item.expiry_date ?? null,
        })),
      }
    );

    if (error) {
      handleError(error, {
        context: "register_po_reception",
        component: "purchaseOrderService",
        metadata: {
          purchase_order_id: data.purchase_order_id,
          plant: data.plant,
        },
      });
      throw error;
    }

    return reception as unknown as PurchaseOrderReception;
  },

  /**
   * Get all receptions for a purchase order
   */
  async getReceptions(
    purchaseOrderId: string
  ): Promise<PurchaseOrderReception[]> {
    const { data, error } = await supabase
      .from("purchase_order_receptions")
      .select(
        `
        *,
        received_by_profile:profiles!received_by(id, name),
        items:purchase_order_reception_items(
          *,
          purchase_order_item:purchase_order_items(
            id,
            description,
            ingredient_type,
            unit,
            quantity,
            oil_type:oil_types(name),
            raw_material:raw_materials(name),
            product:products(short_name)
          )
        )
      `
      )
      .eq("purchase_order_id", purchaseOrderId)
      .order("reception_date", { ascending: false });

    if (error) throw error;
    return data as PurchaseOrderReception[];
  },

  /**
   * Get every reception across all purchase orders, joined to its parent PO
   * (supplier, po_number, order_type). Powers the Ingreso ledger.
   */
  async getAllReceptions(): Promise<ReceptionWithOrder[]> {
    const { data, error } = await supabase
      .from("purchase_order_receptions")
      .select(
        `
        *,
        received_by_profile:profiles!received_by(id, name),
        purchase_order:purchase_orders(
          id,
          po_number,
          order_type,
          currency,
          supplier:suppliers(name)
        ),
        items:purchase_order_reception_items(
          *,
          purchase_order_item:purchase_order_items(
            id,
            description,
            ingredient_type,
            unit
          )
        )
      `
      )
      .order("reception_date", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as ReceptionWithOrder[];
  },

  async cancel(id: string): Promise<PurchaseOrder> {
    return this.updateStatus(id, "cancelled");
  },

  async delete(id: string): Promise<void> {
    // Only allow deleting draft POs
    const po = await this.getById(id);
    if (po.status !== "draft") {
      throw new Error("Solo se pueden eliminar órdenes en estado borrador");
    }

    const { error } = await supabase
      .from("purchase_orders")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  // Get items for a specific PO
  async getItems(purchaseOrderId: string): Promise<PurchaseOrderItem[]> {
    const { data, error } = await supabase
      .from("purchase_order_items")
      .select(
        `
        *,
        oil_type:oil_types(*),
        raw_material:raw_materials(*),
        product:products(id, short_name, barcode, net_price)
      `
      )
      .eq("purchase_order_id", purchaseOrderId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return data as PurchaseOrderItem[];
  },

  // Distinct free-text descriptions used in past Misceláneos ("misc") lines.
  // Powers the creatable combobox so users can reuse prior entries WITHOUT a
  // services master table. Deduped case-insensitively, most-recent first.
  async getMiscDescriptions(): Promise<string[]> {
    const { data, error } = await supabase
      .from("purchase_order_items")
      .select("description")
      .eq("ingredient_type", "misc")
      .order("created_at", { ascending: false })
      .limit(1000);

    if (error) throw error;

    const seen = new Set<string>();
    const result: string[] = [];
    for (const row of data ?? []) {
      const description = (row.description ?? "").trim();
      if (!description) continue;
      const key = description.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(description);
    }
    return result;
  },

  /**
   * Update a reception item (handles stock adjustments automatically)
   */
  async updateReceptionItem(
    receptionItemId: string,
    data: {
      quantity_received: number;
      actual_unit_cost: number;
      expiry_date?: string | null;
    }
  ): Promise<void> {
    const { error } = await supabase.rpc("update_reception_item", {
      p_reception_item_id: receptionItemId,
      p_quantity_received: data.quantity_received,
      p_actual_unit_cost: data.actual_unit_cost,
      p_expiry_date: data.expiry_date ?? null,
    });

    if (error) throw error;
  },

  /**
   * Move all stock from the old plant to the new plant and re-attribute the
   * reception's stock-movement rows. Aborts in the database if the material
   * has already been consumed at the old plant.
   */
  async updateReceptionPlant(
    receptionId: string,
    newPlant: Plant
  ): Promise<void> {
    const { error } = await supabase.rpc("update_reception_plant", {
      p_reception_id: receptionId,
      p_new_plant: newPlant,
    });
    if (error) throw error;
  },

  /**
   * Delete a reception item (reverses stock changes automatically)
   */
  async deleteReceptionItem(receptionItemId: string): Promise<void> {
    const { error } = await supabase.rpc("delete_reception_item", {
      p_reception_item_id: receptionItemId,
    });

    if (error) throw error;
  },
};
